import type { MacroregionName } from '~/config/cities';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { MaybeRefOrGetter, Ref } from 'vue';

import { watchThrottled } from '@vueuse/core';
import {
	usePropertySearchScope,
	usePropertySearchStateKey
} from '~/composables/propertySearch/context';
import { MACROREGIONS } from '~/config/cities';
import { AddressService } from '~/services/AddressService';
import { CityMacroregionHelper } from '~/utils/AddressUtilsWrapper';
import { computed, toValue } from 'vue';

export interface UseSingleFieldSuggestReturn {
	selectedMacroregion: Ref<MacroregionName | (string & {}) | undefined>;
	setMacroregion: (macroregion: MacroregionName | undefined) => void;
	clearMacroregion: () => void;
}

export interface UseSingleFieldSuggestOptions {
	/**
	 * Optional: Filters from the page to automatically sync the macroregion.
	 * When provided, the composable watches for changes and updates selectedMacroregion
	 * based on cities, regions, and explicit macroregions.
	 *
	 * Accepts: Ref, ComputedRef, or getter function
	 */
	filters?: MaybeRefOrGetter<PropertyFilters>;
}

/**
 * Composable to manage search suggest state for single-field scenarios.
 *
 * This is designed for components that:
 * - Have only one search field (not two separate fields)
 * - Need to sync macroregion with page filters or set it manually
 * - Don't need the two-field coordination logic
 *
 * Use cases:
 * - SelectModal: Sets macroregion from property context
 * - Listing pages: Syncs macroregion with URL filters
 * - Any single-field search component
 *
 * @param options - Optional configuration
 * @param options.filters - Page filters to auto-sync macroregion
 *
 * @example
 * // Simple usage in modal (set macroregion manually)
 * const { selectedMacroregion, setMacroregion } = useSingleFieldSuggest();
 * setMacroregion('Curitiba');
 *
 * @example
 * // Auto-sync with page filters (in listing pages)
 * const { filters } = useManager();
 * useSingleFieldSuggest({ filters });
 */
export function useSingleFieldSuggest(
	options?: UseSingleFieldSuggestOptions
): UseSingleFieldSuggestReturn {
	const search = usePropertySearchScope()?.search;
	const selectedMacroregion = search
		? computed({
				get: () => {
					const filters = search.filters.value;
					if (filters.macroregions?.[0]) return filters.macroregions[0];
					if (filters.cities?.[0]) {
						const city = CityMacroregionHelper.getCityInfo(
							filters.cities[0],
							filters.regions?.[0]
						);
						if (city) return city.macroregion;
					}
					return (
						deriveMacroregionFromFilters(filters) || MACROREGIONS.SAO_PAULO
					);
				},
				set: (value: string | undefined) => {
					void search.patch({ macroregions: value ? [value] : undefined });
				}
			})
		: useState<MacroregionName | (string & {}) | undefined>(
				usePropertySearchStateKey('single-field-suggest-macroregion'),
				() => undefined
			);

	/**
	 * Syncs selectedMacroregion from external filters (e.g., from page URL).
	 *
	 * Priority logic:
	 * 1. Explicit macroregion from filters (highest priority)
	 * 2. City + Region → Macroregion
	 * 3. Default to São Paulo
	 *
	 * @param cities - City filters
	 * @param macroregions - Macroregion filters
	 * @param regions - Region filters
	 */
	function syncMacroregionFromFilters(filters: PropertyFilters): void {
		const { cities, macroregions, regions } = filters;

		// Priority 1: Explicit macroregion from filters (most specific intent)
		if (macroregions?.[0]) {
			selectedMacroregion.value = macroregions[0] as MacroregionName;
			return;
		}

		// Priority 2: City + Region → Macroregion (most specific context)
		// Handles cities with region-specific macroregions (e.g., Santana de Parnaíba)
		if (cities?.[0]) {
			const cityInfo = CityMacroregionHelper.getCityInfo(
				cities[0],
				regions?.[0]
			);
			if (cityInfo) {
				selectedMacroregion.value = cityInfo.macroregion as MacroregionName;
				return;
			}
		}

		// Priority 3: Derive macroregion from city and state if available
		const derivedMacroregion = deriveMacroregionFromFilters(filters);
		// Priority 4: Default to São Paulo if no derived macroregion is available
		selectedMacroregion.value = derivedMacroregion || MACROREGIONS.SAO_PAULO;
	}

	/**
	 * Auto-sync with page filters if provided.
	 * Watches for filter changes and updates selectedMacroregion accordingly.
	 */
	if (!search && options?.filters) {
		// Extract filters used by syncMacroregionFromFilters
		const macroregionFilters = computed(() => toValue(options.filters!));

		// Initial sync
		syncMacroregionFromFilters(macroregionFilters.value);

		// Watch for filter changes
		watchThrottled(macroregionFilters, syncMacroregionFromFilters, {
			throttle: 150,
			leading: true
		});
	}

	/**
	 * Manually set macroregion value
	 */
	function setMacroregion(macroregion: MacroregionName | undefined) {
		selectedMacroregion.value = macroregion;
	}

	/**
	 * Clear macroregion value
	 */
	function clearMacroregion() {
		selectedMacroregion.value = undefined;
	}

	function deriveMacroregionFromFilters(
		filters: PropertyFilters
	): string | undefined {
		// If there is no POI parameters, we should not try to derive a macroregion
		if (
			search
				? !Number.isFinite(filters.poiLocationLat) ||
					!Number.isFinite(filters.poiLocationLng)
				: !filters?.poiLocationLat || !filters?.poiLocationLng
		)
			return;

		let city = filters.cities?.[0];
		let state = filters.states?.[0];

		// Fallback to addressSlug if city or state is missing
		if ((!city || !state) && filters.addressSlug) {
			const extractedData = AddressService.extractCityFromAddressSlug(
				filters.addressSlug
			);
			city = extractedData.city;
			state = extractedData.state;
		}

		if (city && state) return AddressUtils.getMacroregionByCity(state, city);
	}

	return {
		selectedMacroregion,
		setMacroregion,
		clearMacroregion
	};
}
